import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFieldValue,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScriptsv2";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldText,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FormCheckbox,
  FormInput,
  FormRadioButton,
  FormSelect,
  FormTextArea,
} from "../common/ControlBundle";
import ExpandDiv from "../common/ExpandDiv";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import {
  getCareModalities,
  getCarePlanDetails,
  getClientCarePlansPromise,
  getDiagnosisDetails,
  getFormsySubmissionsByFormIdForClient,
} from "../services/soar";
import { getSchema } from "./joi/validation";

function ALTFCSemiAnnual() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [loadStatus, setLoadStatus] = useState({});
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialProgram?.ccx_client_programid && loadStatus.dataSet) {
      setData((d) => {
        const obj = { ...d };

        if (
          obj.SelectedProgram === undefined ||
          getFieldValue(obj.SelectedProgram)
        ) {
          return obj;
        }

        obj.SelectedProgram.value = initialProgram.ccx_client_programid;
        return obj;
      });

      setFormsyDetails((fd) => {
        const details = { ...fd };

        if (details.ClientProgramId === initialProgram.ccx_client_programid) {
          return details;
        }

        details.ClientProgramId = initialProgram.ccx_client_programid;
        details.ClientProgramName = getFieldName(
          initialProgram.ccx_programunit
        );
        return details;
      });
    }
  }, [initialProgram, loadStatus.dataSet]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      !safyInfo?.user ||
      loadStatus.configLoaded ||
      loadStatus.loadingConfig
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            dataSet: true,
          };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    formid,
    clientid,
    safyInfo,
    submissionid,
    loadStatus.configLoaded,
    loadStatus.loadingConfig,
  ]);

  const helperConfiguration = useMemo(() => {
    const defaultConfig = {
      buttonBarConfig: {
        sign: {
          display: false,
          initialSignatureType: "full",
        },
        clone: {
          display: false,
        },
        delete: {
          display: false,
        },
        share: {
          display: false,
        },
        comment: {
          display: false,
        },
      },
      signatureType: "full",
      finalSignType: "Provider",
    };
    if (!formsyConfig.HelperConfiguration?.[0]) {
      return defaultConfig;
    }
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      console.log(
        "helperConfiguration JSON:",
        formsyConfig.HelperConfiguration[0]
      );
      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments; // commentsOverride is usually null, but specifically undefined during Final Signing
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.value) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property].type !== "section" &&
        data[property].type !== "label" &&
        data[property].type !== "User" &&
        data[property].type !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property].label !== undefined && data[property].label !== ""
              ? data[property].label
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    if (helperConfiguration.finalSignType === "Any") {
      return true;
    }

    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");
    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );
    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(undefined);

  useEffect(() => {
    if (
      submissionid ||
      !clientid ||
      !data.SelectedProgram?.value ||
      loadStatus.loadingRecords ||
      loadStatus.recordsLoaded
    ) {
      return;
    }

    const loadClientRecords = async () => {
      setLoadStatus((ls) => ({ ...ls, loadingRecords: true }));

      const results = await Promise.all([
        getCareModalities(clientid),
        getClientCarePlansPromise(clientid),
      ]);

      if (!isMountedRef.current) {
        return;
      }

      if (results?.length === 2) {
        setData((d) => {
          const obj = { ...d };

          const sortedModalities =
            results[0]?.length > 0 &&
            results[0].sort((a, b) => {
              const aStart = moment(getFieldDate(a.ccx_startdate));
              const bStart = moment(getFieldDate(b.ccx_startdate));

              if (aStart.isAfter(bStart)) return 1;
              if (bStart.isAfter(aStart)) return -1;
              return 0;
            });

          if (sortedModalities) {
            const modality = sortedModalities.find(
              (modality) =>
                getFieldText(modality.ccx_clientprogramid) ===
                data.SelectedProgram.value
            );

            if (modality?.ccx_startdate) {
              obj.DatePlacedTFC.value = moment(
                getFieldDate(modality.ccx_startdate)
              ).format("L");
            }
          }

          return obj;
        });

        if (results[1]?.length > 0) {
          const loadCarePlanGoals = async () => {
            let goals = "";
            const selectedCarePlan = results[1]
              .sort((a, b) => {
                const aDate = moment(getFieldDate(a.ccx_effectivedate));
                const bDate = moment(getFieldDate(b.ccx_effectivedate));

                return aDate.isAfter(bDate)
                  ? 1
                  : aDate.isBefore(bDate)
                  ? -1
                  : 0;
              })
              .find(
                (carePlan) =>
                  getFieldText(carePlan.ccx_clientprogram) ===
                  data.SelectedProgram.value
              );

            if (selectedCarePlan?.ccx_careplanid) {
              const carePlan = await getCarePlanDetails(
                selectedCarePlan.ccx_careplanid
              );

              goals = carePlan.plan_goals.map((goal) => goal.ccx_Description)
                .join(`
      `);
            }

            return goals;
          };

          const loadPSD = async () => {
            const psdResults = await getFormsySubmissionsByFormIdForClient(
              "00C57AC1-7F99-EC11-A507-0050F21C6F69", // AL PSD Form Container Id
              clientid
            );

            if (psdResults) {
              const parsedPSD = JSON.parse(psdResults);

              if (parsedPSD?.FormSubmissions?.Submission?.length > 0) {
                const lastFinalSignedPSD =
                  parsedPSD.FormSubmissions.Submission.sort((a, b) => {
                    const aDate = moment(a.CreatedOn);
                    const bDate = moment(b.CreatedOn);

                    return aDate.isAfter(bDate)
                      ? 1
                      : aDate.isBefore(bDate)
                      ? -1
                      : 0;
                  }).find(
                    (psd) =>
                      psd.FinalSigned === "1" &&
                      psd.ClientProgramId === data.SelectedProgram.value
                  );

                return lastFinalSignedPSD;
              }
            }
          };

          const executePromise = async () => {
            const results = await Promise.all([
              getDiagnosisDetails(data.SelectedProgram.value),
              loadCarePlanGoals(),
              loadPSD(),
            ]);

            if (!isMountedRef.current) {
              return;
            }

            if (results) {
              setData((d) => {
                const obj = { ...d };
                const diagnosisDetails = results[0];
                const goals = results[1];
                const lastPSD = results[2];

                if (diagnosisDetails?.length > 0) {
                  obj.Diagnoses.value = diagnosisDetails.map(
                    (dd) => dd["dx.ccx_name"]
                  ).join(`
      `);
                }

                if (lastPSD) {
                  const psdResults = lastPSD.FormResults.results;

                  obj.ADLNA.value = getFieldValue(psdResults.ADLNA);
                  obj.Hygiene.value = getFieldValue(psdResults.Hygiene);
                  obj.HygieneNeeded.value = getFieldValue(
                    psdResults.HygieneNeeded
                  );
                  obj.Housekeeping.value = getFieldValue(
                    psdResults.Housekeeping
                  );
                  obj.HousekeepingNeeded.value = getFieldValue(
                    psdResults.HousekeepingNeeded
                  );
                  obj.MedicationManagement.value = getFieldValue(
                    psdResults.MedicationManagement
                  );
                  obj.MedicationManagementNeeded.value = getFieldValue(
                    psdResults.MedicationManagementNeeded
                  );
                  obj.Transportation.value = getFieldValue(
                    psdResults.Transportation
                  );
                  obj.TransportationNeeded.value = getFieldValue(
                    psdResults.TransportationNeeded
                  );
                  obj.BehaviorEducation.value = getFieldValue(
                    psdResults.BehaviorEducation
                  );
                  obj.BehaviorEducationNeeded.value = getFieldValue(
                    psdResults.BehaviorEducationNeeded
                  );
                  obj.StressManagement.value = getFieldValue(
                    psdResults.StressManagement
                  );
                  obj.StressManagementNeeded.value = getFieldValue(
                    psdResults.StressManagementNeeded
                  );
                  obj.HealthyLifestyle.value = getFieldValue(
                    psdResults.HealthyLifestyle
                  );
                  obj.HealthyLifestyleNeeded.value = getFieldValue(
                    psdResults.HealthyLifestyleNeeded
                  );
                  obj.Laundry.value = getFieldValue(psdResults.Laundry);
                  obj.LaundryNeeded.value = getFieldValue(
                    psdResults.LaundryNeeded
                  );
                  obj.MealPrep.value = getFieldValue(psdResults.MealPrep);
                  obj.MealPrepNeeded.value = getFieldValue(
                    psdResults.MealPrepNeeded
                  );
                  obj.MoneyManagement.value = getFieldValue(
                    psdResults.MoneyManagement
                  );
                  obj.MoneyManagementNeeded.value = getFieldValue(
                    psdResults.MoneyManagementNeeded
                  );
                  obj.Shopping.value = getFieldValue(psdResults.Shopping);
                  obj.ShoppingNeeded.value = getFieldValue(
                    psdResults.ShoppingNeeded
                  );
                  obj.BasicSkillsComments.value = getFieldValue(
                    psdResults.BasicSkillsComments
                  );
                  obj.RelationshipsNA.value = getFieldValue(
                    psdResults.RelationshipsNA
                  );
                  obj.CoopSkills.value = getFieldValue(psdResults.CoopSkills);
                  obj.CoopSkillsDetails.value = getFieldValue(
                    psdResults.CoopSkillsDetails
                  );
                  obj.CommunicationSkills.value = getFieldValue(
                    psdResults.CommunicationSkills
                  );
                  obj.CommunicationSkillsDetails.value = getFieldValue(
                    psdResults.CommunicationSkillsDetails
                  );
                  obj.EmotionalUnderstanding.value = getFieldValue(
                    psdResults.EmotionalUnderstanding
                  );
                  obj.EmotionalUnderstandingDetails.value = getFieldValue(
                    psdResults.EmotionalUnderstandingDetails
                  );
                  obj.ProblemSolving.value = getFieldValue(
                    psdResults.ProblemSolving
                  );
                  obj.ProblemSolvingDetails.value = getFieldValue(
                    psdResults.ProblemSolvingDetails
                  );
                  obj.ConflictResolution.value = getFieldValue(
                    psdResults.ConflictResolution
                  );
                  obj.ConflictResolutionDetails.value = getFieldValue(
                    psdResults.ConflictResolutionDetails
                  );
                  obj.DecisionMakingSkills.value = getFieldValue(
                    psdResults.DecisionMakingSkills
                  );
                  obj.DecisionMakingSkillsDetails.value = getFieldValue(
                    psdResults.DecisionMakingSkillsDetails
                  );
                  obj.Bullied.value = getFieldValue(psdResults.Bullied);
                  obj.BulliedDetails.value = getFieldValue(
                    psdResults.BulliedDetails
                  );
                  obj.Bully.value = getFieldValue(psdResults.Bully);
                  obj.BullyDetails.value = getFieldValue(
                    psdResults.BullyDetails
                  );
                  obj.AcceptedByPeers.value = getFieldValue(
                    psdResults.AcceptedByPeers
                  );
                  obj.AcceptedByPeersDetails.value = getFieldValue(
                    psdResults.AcceptedByPeersDetails
                  );
                  obj.NegativeAssociations.value = getFieldValue(
                    psdResults.NegativeAssociations
                  );
                  obj.NegativeAssociationsDetails.value = getFieldValue(
                    psdResults.NegativeAssociationsDetails
                  );
                }

                obj.Goals.value = goals;
                return obj;
              });
            }
          };

          executePromise();
        }
      }

      setLoadStatus((ls) => ({
        ...ls,
        loadingRecords: false,
        recordsLoaded: true,
      }));
    };

    loadClientRecords();
  }, [
    submissionid,
    clientid,
    data.SelectedProgram?.value,
    clientData.programs,
    loadStatus.loadingRecords,
    loadStatus.recordsLoaded,
  ]);

  const providerOptions = [
    { _id: "Alabama Clinical Schools", name: "Alabama Clinical Schools" },
    { _id: "Alabama Mentor/Sevita", name: "Alabama Mentor/Sevita" },
    { _id: "Camelia", name: "Camelia" },
    { _id: "Embrace", name: "Embrace" },
    { _id: "Gateway", name: "Gateway" },
    { _id: "Health Connect", name: "Health Connect" },
    { _id: "LCYDC", name: "LCYDC" },
    { _id: "SAFY", name: "SAFY" },
    { _id: "Seraaj", name: "Seraaj" },
  ];

  const tierOptions = [
    { _id: "1", name: "1. Severe" },
    { _id: "2", name: "2. Significant to Severe" },
    { _id: "3", name: "3. Significant" },
    { _id: "4", name: "4. Elevated" },
    { _id: "5", name: "5. TFC Core Services+" },
    { _id: "6", name: "6. TFC Core Services" },
  ];

  const costPlanOptions = [
    {
      _id: "1",
      name: (
        <>
          <b>Severe</b> behavioral/emotional/mental illness that requires
          additional services (supervision, healthcare, nursing, transportation,
          equipment for child/home to remain safe, behavioral aide 24/7, intense
          behavioral therapy).
        </>
      ),
    },
    {
      _id: "2",
      name: (
        <>
          <b>Significant to Severe</b> behavioral/emotional/mental illness that
          requires additional services (supervision, healthcare, nursing,
          transportation, equipment, behavioral aide for awake hours, intense
          behavioral therapy).
        </>
      ),
    },
    {
      _id: "3",
      name: (
        <>
          <b>Significant</b> behavioral/emotional/mental illness that requires
          additional services (supervision, healthcare, transportation,
          equipment, behavioral aide for long periods of time, behavioral
          therapy).
        </>
      ),
    },
    {
      _id: "4",
      name: (
        <>
          <b>Elevated</b> behavioral/emotional issues that require additional
          services (transportation, additional supervision/care, behavior
          therapy, equipment, behavioral aide 2-3x per week).
        </>
      ),
    },
    {
      _id: "5",
      name: (
        <>
          TFC Core Services and some additional services, including
          transportation, behavioral therapy, or additonal supervision.
        </>
      ),
    },
    {
      _id: "6",
      name: <>Core Services only.</>,
    },
  ];

  const permanencyPlanOptions = [
    { _id: "Return to Parent", name: "Return to Parent" },
    {
      _id: "Permanent Relative Placement with Transfer of Custody to the Relative",
      name: "Permanent Relative Placement with Transfer of Custody to the Relative",
    },
    {
      _id: "Permanent Relative Custody with DHR Retaining Custody",
      name: "Permanent Relative Custody with DHR Retaining Custody",
    },
    {
      _id: "Adoption by Current Foster Parent",
      name: "Adoption by Current Foster Parent",
    },
    {
      _id: "Adoption with No Identified Resource",
      name: "Adoption with No Identified Resource",
    },
    { _id: "Kinship Guardianship", name: "Kinship Guardianship" },
    { _id: "APPLA", name: "APPLA" },
    { _id: "Adult Custodial Care", name: "Adult Custodial Care" },
  ];

  const concurrentPlanOptions = [
    { _id: "Return to Parent", name: "Return to Parent" },
    {
      _id: "Permanent Relative Placement with Transfer of Custody to the Relative",
      name: "Permanent Relative Placement with Transfer of Custody to the Relative",
    },
    {
      _id: "Permanent Relative Custody with DHR Retaining Custody",
      name: "Permanent Relative Custody with DHR Retaining Custody",
    },
    {
      _id: "Adoption by Current Foster Parent",
      name: "Adoption by Current Foster Parent",
    },
    {
      _id: "Adoption with No Identified Resource",
      name: "Adoption with No Identified Resource",
    },
    { _id: "APPLA", name: "APPLA" },
    { _id: "Adult Custodial Care", name: "Adult Custodial Care" },
  ];

  const medicaidWaiverStatusOptions = [
    { _id: "Approved", name: "Approved" },
    { _id: "Application Pending", name: "Application Pending" },
    { _id: "No Application Made", name: "No Application Made" },
    { _id: "N/A", name: "N/A" },
    { _id: "Unknown", name: "Unknown" },
  ];

  const functioningLevelOptions = [
    { _id: "Appropriate", name: "Appropriate" },
    { _id: "Problematic", name: "Problematic" },
    { _id: "N/A", name: "N/A" },
  ];

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const yesNoNoneNAOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "None", name: "None" },
    { _id: "N/A", name: "N/A" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <FormSelect
                    name="Provider"
                    label="Provider:"
                    error={errors.Provider}
                    value={getFieldValue(data.Provider)}
                    options={providerOptions}
                    onChange={handleChange}
                    identical={compare("Provider")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormInput
                    name="County"
                    label="County:"
                    error={errors.County}
                    value={getFieldValue(data.County)}
                    onChange={handleChange}
                    identical={compare("County")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="Dates"
                    label="Dates Covered:"
                    error={errors.Dates}
                    value={getFieldValue(data.Dates)}
                    onChange={handleChange}
                    identical={compare("Dates")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormSelect
                    name="CurrentTier"
                    label="Current Tier Rate:"
                    error={errors.CurrentTier}
                    value={getFieldValue(data.CurrentTier)}
                    options={tierOptions}
                    onChange={handleChange}
                    identical={compare("CurrentTier")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="ChildIQ"
                    label="Child's IQ:"
                    error={errors.ChildIQ}
                    value={getFieldValue(data.ChildIQ)}
                    onChange={handleChange}
                    identical={compare("ChildIQ")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormInput
                    name="DatePlacedTFC"
                    label="Date Placed in TFC-E:"
                    error={errors.DatePlacedTFC}
                    value={getFieldValue(data.DatePlacedTFC)}
                    onChange={handleChange}
                    identical={compare("DatePlacedTFC")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="DatePlacedCurrent"
                    label="Date Placed in Current Placement:"
                    error={errors.DatePlacedCurrent}
                    value={getFieldValue(data.DatePlacedCurrent)}
                    onChange={handleChange}
                    identical={compare("DatePlacedCurrent")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormInput
                    name="DateLastISP"
                    label="Date of Last ISP:"
                    error={errors.DateLastISP}
                    value={getFieldValue(data.DateLastISP)}
                    onChange={handleChange}
                    identical={compare("DateLastISP")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormSelect
                    name="PermanencyPlan"
                    label="PermanencyPlan:"
                    error={errors.PermanencyPlan}
                    value={getFieldValue(data.PermanencyPlan)}
                    options={permanencyPlanOptions}
                    onChange={handleChange}
                    identical={compare("PermanencyPlan")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormSelect
                    name="ConcurrentPlan"
                    label="ConcurrentPlan:"
                    error={errors.ConcurrentPlan}
                    value={getFieldValue(data.ConcurrentPlan)}
                    options={concurrentPlanOptions}
                    onChange={handleChange}
                    identical={compare("ConcurrentPlan")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormSelect
                    name="MedicaidWaiverStatus"
                    label="Medicaid Waiver Status, if applicable:"
                    error={errors.MedicaidWaiverStatus}
                    value={getFieldValue(data.MedicaidWaiverStatus)}
                    options={medicaidWaiverStatusOptions}
                    onChange={handleChange}
                    identical={compare("MedicaidWaiverStatus")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormRadioButton
                    name="ReceiveSSI"
                    label="Does Child Receive SSI?"
                    error={errors.ReceiveSSI}
                    value={getFieldValue(data.ReceiveSSI)}
                    options={yesNoOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.ReceiveSSI) !== "No" ? (
                    <FormInput
                      name="ReceiveSSIDetails"
                      label="If so, for what Reason?"
                      error={errors.ReceiveSSIDetails}
                      value={getFieldValue(data.ReceiveSSIDetails)}
                      onChange={handleChange}
                      identical={compare("ReceiveSSIDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                </div>
              </div>
              <FormInput
                name="Diagnoses"
                label="Diagnoses:"
                error={errors.Diagnoses}
                value={getFieldValue(data.Diagnoses)}
                onChange={handleChange}
                identical={compare("Diagnoses")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormInput
                name="MedicalDiagnoses"
                label="Medical Diagnoses, if applicable:"
                error={errors.MedicalDiagnoses}
                value={getFieldValue(data.MedicalDiagnoses)}
                onChange={handleChange}
                identical={compare("MedicalDiagnoses")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormInput
                name="Goals"
                label="Child's Treatment Plan Goals:"
                error={errors.Goals}
                value={getFieldValue(data.Goals)}
                onChange={handleChange}
                identical={compare("Goals")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormInput
                name="Progress"
                label="Description of the Child's Progress:"
                error={errors.Progress}
                value={getFieldValue(data.Progress)}
                onChange={handleChange}
                identical={compare("Progress")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormInput
                name="Barriers"
                label="Barriers to Progress:"
                error={errors.Barriers}
                value={getFieldValue(data.Barriers)}
                onChange={handleChange}
                identical={compare("Barriers")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h6">Adaptive Functioning Measurement</div>
              <ExpandDiv
                id="Activities"
                expandStatus={expandStatus}
                className="mt-2"
                title="Basic Living Skills"
                tooltip="This section is not asking about teaching basic living skills. This section is asking about restoring basic living skills that had been achieved, have since been lost, and now needs to be restored."
              >
                <FormCheckbox
                  name="ADLNA"
                  label="N/A"
                  checked={getFieldValue(data.ADLNA) === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
                {getFieldValue(data.ADLNA) !== "true" ? (
                  <>
                    <div className="container">
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mb-2">
                        <div className="col">
                          <b>Skill Areas</b>
                        </div>
                        <div className="col">
                          <b>Skill Achieved</b>
                        </div>
                        <div className="col">
                          <b>Restorative Services Needed</b>
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Personal Hygiene:</div>
                        <div className="col">
                          <FormRadioButton
                            name="Hygiene"
                            error={errors.Hygiene}
                            value={getFieldValue(data.Hygiene)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="HygieneNeeded"
                            error={errors.HygieneNeeded}
                            value={getFieldValue(data.HygieneNeeded)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Housekeeping:</div>
                        <div className="col">
                          <FormRadioButton
                            name="Housekeeping"
                            error={errors.Housekeeping}
                            value={getFieldValue(data.Housekeeping)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="HousekeepingNeeded"
                            error={errors.HousekeepingNeeded}
                            value={getFieldValue(data.HousekeepingNeeded)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Medication Management:</div>
                        <div className="col">
                          <FormRadioButton
                            name="MedicationManagement"
                            error={errors.MedicationManagement}
                            value={getFieldValue(data.MedicationManagement)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="MedicationManagementNeeded"
                            error={errors.MedicationManagementNeeded}
                            value={getFieldValue(
                              data.MedicationManagementNeeded
                            )}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Use of Public Transportation:</div>
                        <div className="col">
                          <FormRadioButton
                            name="Transportation"
                            error={errors.Transportation}
                            value={getFieldValue(data.Transportation)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="TransportationNeeded"
                            error={errors.TransportationNeeded}
                            value={getFieldValue(data.TransportationNeeded)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Behavior Education:</div>
                        <div className="col">
                          <FormRadioButton
                            name="BehaviorEducation"
                            error={errors.BehaviorEducation}
                            value={getFieldValue(data.BehaviorEducation)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="BehaviorEducationNeeded"
                            error={errors.BehaviorEducationNeeded}
                            value={getFieldValue(data.BehaviorEducationNeeded)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Stress Management:</div>
                        <div className="col">
                          <FormRadioButton
                            name="StressManagement"
                            error={errors.StressManagement}
                            value={getFieldValue(data.StressManagement)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="StressManagementNeeded"
                            error={errors.StressManagementNeeded}
                            value={getFieldValue(data.StressManagementNeeded)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Healthy Lifestyle:</div>
                        <div className="col">
                          <FormRadioButton
                            name="HealthyLifestyle"
                            error={errors.HealthyLifestyle}
                            value={getFieldValue(data.HealthyLifestyle)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="HealthyLifestyleNeeded"
                            error={errors.HealthyLifestyleNeeded}
                            value={getFieldValue(data.HealthyLifestyleNeeded)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Laundry:</div>
                        <div className="col">
                          <FormRadioButton
                            name="Laundry"
                            error={errors.Laundry}
                            value={getFieldValue(data.Laundry)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="LaundryNeeded"
                            error={errors.LaundryNeeded}
                            value={getFieldValue(data.LaundryNeeded)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Meal Preparation:</div>
                        <div className="col">
                          <FormRadioButton
                            name="MealPrep"
                            error={errors.MealPrep}
                            value={getFieldValue(data.MealPrep)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="MealPrepNeeded"
                            error={errors.MealPrepNeeded}
                            value={getFieldValue(data.MealPrepNeeded)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Money management:</div>
                        <div className="col">
                          <FormRadioButton
                            name="MoneyManagement"
                            error={errors.MoneyManagement}
                            value={getFieldValue(data.MoneyManagement)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="MoneyManagementNeeded"
                            error={errors.MoneyManagementNeeded}
                            value={getFieldValue(data.MoneyManagementNeeded)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Shopping:</div>
                        <div className="col">
                          <FormRadioButton
                            name="Shopping"
                            error={errors.Shopping}
                            value={getFieldValue(data.Shopping)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="ShoppingNeeded"
                            error={errors.ShoppingNeeded}
                            value={getFieldValue(data.ShoppingNeeded)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormTextArea
                            name="BasicSkillsComments"
                            label="Comments:"
                            error={errors.BasicSkillsComments}
                            value={getFieldValue(data.BasicSkillsComments)}
                            onChange={handleChange}
                            identical={compare("BasicSkillsComments")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="Friendships"
                expandStatus={expandStatus}
                className="mt-2"
                title="Friendship/ Social Peer Support/ Relationships"
              >
                <FormCheckbox
                  name="RelationshipsNA"
                  label="N/A (Skip Section)"
                  checked={getFieldValue(data.RelationshipsNA) === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
                {getFieldValue(data.RelationshipsNA) !== "true" ? (
                  <>
                    <div className="container">
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom">
                        <div className="col">
                          <b>Skill</b>
                        </div>
                        <div className="col">
                          <b>Functioning Level</b>
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">Cooperative play skills:</div>
                        <div className="col">
                          <FormRadioButton
                            name="CoopSkills"
                            error={errors.CoopSkills}
                            value={getFieldValue(data.CoopSkills)}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {getFieldValue(data.CoopSkills) === "Problematic" ? (
                            <FormInput
                              name="CoopSkillsDetails"
                              label="Describe:"
                              error={errors.CoopSkillsDetails}
                              value={getFieldValue(data.CoopSkillsDetails)}
                              onChange={handleChange}
                              identical={compare("CoopSkillsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">
                          Communication and interaction skills:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="CommunicationSkills"
                            error={errors.CommunicationSkills}
                            value={getFieldValue(data.CommunicationSkills)}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {getFieldValue(data.CommunicationSkills) ===
                          "Problematic" ? (
                            <FormInput
                              name="CommunicationSkillsDetails"
                              label="Describe:"
                              error={errors.CommunicationSkillsDetails}
                              value={getFieldValue(
                                data.CommunicationSkillsDetails
                              )}
                              onChange={handleChange}
                              identical={compare("CommunicationSkillsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">
                          Emotional understanding and self-regulation skills:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="EmotionalUnderstanding"
                            error={errors.EmotionalUnderstanding}
                            value={getFieldValue(data.EmotionalUnderstanding)}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {getFieldValue(data.EmotionalUnderstanding) ===
                          "Problematic" ? (
                            <FormInput
                              name="EmotionalUnderstandingDetails"
                              label="Describe:"
                              error={errors.EmotionalUnderstandingDetails}
                              value={getFieldValue(
                                data.EmotionalUnderstandingDetails
                              )}
                              onChange={handleChange}
                              identical={compare(
                                "EmotionalUnderstandingDetails"
                              )}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">
                          Social problem-solving skills:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="ProblemSolving"
                            error={errors.ProblemSolving}
                            value={getFieldValue(data.ProblemSolving)}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {getFieldValue(data.ProblemSolving) ===
                          "Problematic" ? (
                            <FormInput
                              name="ProblemSolvingDetails"
                              label="Describe:"
                              error={errors.ProblemSolvingDetails}
                              value={getFieldValue(data.ProblemSolvingDetails)}
                              onChange={handleChange}
                              identical={compare("ProblemSolvingDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">Conflict resolution skills:</div>
                        <div className="col">
                          <FormRadioButton
                            name="ConflictResolution"
                            error={errors.ConflictResolution}
                            value={getFieldValue(data.ConflictResolution)}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {getFieldValue(data.ConflictResolution) ===
                          "Problematic" ? (
                            <FormInput
                              name="ConflictResolutionDetails"
                              label="Describe:"
                              error={errors.ConflictResolutionDetails}
                              value={getFieldValue(
                                data.ConflictResolutionDetails
                              )}
                              onChange={handleChange}
                              identical={compare("ConflictResolutionDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">Decision making skills:</div>
                        <div className="col">
                          <FormRadioButton
                            name="DecisionMakingSkills"
                            error={errors.DecisionMakingSkills}
                            value={getFieldValue(data.DecisionMakingSkills)}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {getFieldValue(data.DecisionMakingSkills) ===
                          "Problematic" ? (
                            <FormInput
                              name="DecisionMakingSkillsDetails"
                              label="Describe:"
                              error={errors.DecisionMakingSkillsDetails}
                              value={getFieldValue(
                                data.DecisionMakingSkillsDetails
                              )}
                              onChange={handleChange}
                              identical={compare("DecisionMakingSkillsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <FormRadioButton
                      name="Bullied"
                      label="History of being bullied:"
                      error={errors.Bullied}
                      value={getFieldValue(data.Bullied)}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {getFieldValue(data.Bullied) === "Yes" ? (
                      <FormInput
                        name="BulliedDetails"
                        label="Describe:"
                        error={errors.BulliedDetails}
                        value={getFieldValue(data.BulliedDetails)}
                        onChange={handleChange}
                        identical={compare("BulliedDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="Bully"
                      label="History of bullying others:"
                      error={errors.Bully}
                      value={getFieldValue(data.Bully)}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {getFieldValue(data.Bully) === "Yes" ? (
                      <FormInput
                        name="BullyDetails"
                        label="Describe:"
                        error={errors.BullyDetails}
                        value={getFieldValue(data.BullyDetails)}
                        onChange={handleChange}
                        identical={compare("BullyDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="AcceptedByPeers"
                      label="Accepted by peer group:"
                      error={errors.AcceptedByPeers}
                      value={getFieldValue(data.AcceptedByPeers)}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {getFieldValue(data.AcceptedByPeers) === "No" && (
                      <FormInput
                        name="AcceptedByPeersDetails"
                        label="Comments:"
                        error={errors.AcceptedByPeersDetails}
                        value={getFieldValue(data.AcceptedByPeersDetails)}
                        onChange={handleChange}
                        identical={compare("AcceptedByPeersDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <FormRadioButton
                      name="NegativeAssociations"
                      label="Negative peer group associations:"
                      error={errors.NegativeAssociations}
                      value={getFieldValue(data.NegativeAssociations)}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {getFieldValue(data.NegativeAssociations) === "Yes" && (
                      <FormInput
                        name="NegativeAssociationsDetails"
                        label="Comments:"
                        error={errors.NegativeAssociationsDetails}
                        value={getFieldValue(data.NegativeAssociationsDetails)}
                        onChange={handleChange}
                        identical={compare("NegativeAssociationsDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="CostPlan"
                expandStatus={expandStatus}
                className="mt-2"
                title="Cost Plan for the Next Six Months of Care"
              >
                <FormSelect
                  name="CostPlan"
                  error={errors.CostPlan}
                  value={getFieldValue(data.CostPlan)}
                  options={tierOptions}
                  onChange={handleChange}
                  identical={compare("CostPlan")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="w-25 mt-1 d-print-none"
                />
                <p>
                  <div className="d-print-none">
                    In addtion to an Axis I Diagnosis, intellectual disability,
                    or chronic medical condition, this youth has/requires:
                    <ol>
                      {costPlanOptions.map((item) => (
                        <li key={item._id}>{item.name}</li>
                      ))}
                    </ol>
                  </div>
                  <div className="d-none d-print-block">
                    In addtion to an Axis I Diagnosis, intellectual disability,
                    or chronic medical condition, this youth{" "}
                    {getFieldValue(data.CostPlan) === "5" ||
                    getFieldValue(data.CostPlan) === "6"
                      ? "requires"
                      : "has"}
                    :<br />
                    &bull;&nbsp;&nbsp;
                    {
                      costPlanOptions.filter(
                        (item) => item._id === getFieldValue(data.CostPlan)
                      )[0]?.name
                    }
                  </div>
                </p>
                <p>
                  <b>
                    Based on the needs,{" "}
                    {clientData.client !== undefined ? (
                      clientData.client.fullName
                    ) : (
                      <i>(Recipient)</i>
                    )}{" "}
                    will be in{" "}
                    {getFieldValue(data.CostPlan) !== undefined ? (
                      <>Tier {getFieldValue(data.CostPlan)}</>
                    ) : (
                      <i>(Tier x)</i>
                    )}{" "}
                    services for the next 6 months.
                  </b>
                </p>
                <FormInput
                  name="NextSteps"
                  label="Next Steps:"
                  error={errors.NextSteps}
                  value={getFieldValue(data.NextSteps)}
                  onChange={handleChange}
                  identical={compare("NextSteps")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default ALTFCSemiAnnual;
